import { Show } from '@agro1desenvolvimento/agro1-design-system/components/atoms';
import { Skeleton } from '@agro1desenvolvimento/agro1-design-system/components/ui';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { AppTopbar } from '@/components/app-topbar';
import { NoticeDialog } from '@/components/notice-dialog';
import { PageLayout } from '@/components/page-layout';
import { UnsavedChangesDialog } from '@/components/unsaved-changes-dialog';
import { formatDate } from '@/lib/format';
import { ProducerSelect } from '@/modules/lab-shared/components/producer-select';
import {
  SampleDetailCard,
  type SampleField,
} from '@/modules/lab-shared/components/sample-detail-card';
import { SamplePhotosStrip } from '@/modules/lab-shared/components/sample-photos-strip';
import { SyncFailureNotice } from '@/modules/lab-shared/components/sync-failure-notice';
import { useAnalysts } from '@/modules/lab-shared/hooks/use-analysts';
import { analysisDetailSearchSchema, toListSearch } from '@/modules/lab-shared/lib/list-search';
import { SandBedAnalysisForm } from '@/modules/sand-bed/components/sand-bed-analysis-form';
import { SandBedResultSummary } from '@/modules/sand-bed/components/sand-bed-result-summary';
import { type SandBedAnalysisData } from '@/modules/sand-bed/services/sand-bed-types';
import { useSandBedDetailViewModel } from '@/modules/sand-bed/view-models/use-sand-bed-detail-view-model';

function SandBedDetailPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = Route.useParams();
  const listSearch = Route.useSearch();
  const { data: analysts = [] } = useAnalysts();
  const [expanded, setExpanded] = useState(true);
  const [completionInput, setCompletionInput] = useState<SandBedAnalysisData | null>(null);
  const {
    detail,
    hasProducer,
    isLoading,
    error,
    isReadOnly,
    images,
    isSaving,
    saveAnalysis,
    completeAnalysis,
    readHasUnsavedChanges,
    setHasUnsavedChanges,
  } = useSandBedDetailViewModel(id);

  const goBack = () =>
    void navigate({ to: '/seeds', search: toListSearch(listSearch, 'canteiro-areia') });

  const confirmCompletion = () => {
    if (!completionInput) return;

    completeAnalysis(completionInput, goBack);
    setCompletionInput(null);
  };

  if (!hasProducer) {
    return (
      <PageLayout>
        <AppTopbar
          actions={<ProducerSelect />}
          breadcrumb={t('lab.detail.sandBedTitle')}
          title={t('lab.title')}
          onTitleClick={goBack}
        />
        <p className="py-10 text-center text-sm text-muted-foreground">
          {t('lab.noProducerSelected')}
        </p>
      </PageLayout>
    );
  }

  if (isLoading) {
    return (
      <PageLayout>
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-40 w-full" />
      </PageLayout>
    );
  }

  if (error || !detail) {
    return (
      <PageLayout>
        <p className="text-destructive py-10 text-center text-sm">{t('sandBed.loadError')}</p>
      </PageLayout>
    );
  }

  const { analysis } = detail;

  const primaryFields: SampleField[] = [
    { label: t('lab.table.requester'), value: detail.requester },
    { label: t('tetrazolium.sample.internalId'), value: detail.sampleId },
    { label: t('lab.table.species'), value: detail.species },
    { label: t('sandBed.sample.lot'), value: detail.lot },
    { label: t('sandBed.sample.harvest'), value: detail.harvest },
  ];

  const secondaryFields: SampleField[] = [
    { label: t('sandBed.sample.scientificName'), value: detail.scientificName },
    { label: t('sandBed.sample.cultivar'), value: detail.cultivar },
    { label: t('sandBed.sample.category'), value: detail.category },
    { label: t('sandBed.sample.entryDate'), value: formatDate(detail.entryDate) },
    { label: t('sandBed.sample.samplingDate'), value: formatDate(detail.samplingDate) },
  ];

  return (
    <PageLayout>
      <AppTopbar
        actions={<ProducerSelect />}
        breadcrumb={t(isReadOnly ? 'lab.detail.viewTitle' : 'lab.detail.sandBedTitle')}
        title={t('lab.title')}
        onTitleClick={goBack}
      />

      <SyncFailureNotice errors={detail.syncErrors} />

      <SampleDetailCard
        expanded={expanded}
        photos={
          <SamplePhotosStrip
            disabled={images.isUploading}
            photos={images.images.map(({ id: imageId, url, fullsizeUrl, name }) => ({
              id: imageId,
              fullsizeUrl,
              url,
              name,
            }))}
            readOnly={isReadOnly}
            onRemove={(photoId) => images.remove(photoId)}
            onUpload={(files) => images.upload(files)}
          />
        }
        primaryFields={primaryFields}
        sampleNumber={detail.sampleId}
        secondaryFields={secondaryFields}
        status={detail.status}
        onToggle={() => setExpanded((current) => !current)}
      />

      <Show when={isReadOnly}>
        <SandBedResultSummary
          analysis={analysis}
          analystName={
            analysts.find(({ id: analystId }) => analystId === analysis?.analystId)?.name ?? ''
          }
          onBack={goBack}
        />
      </Show>

      <Show when={!isReadOnly}>
        <SandBedAnalysisForm
          defaultValues={analysis}
          submitting={isSaving}
          onBack={goBack}
          onComplete={(input) => setCompletionInput(input)}
          onDirtyChange={setHasUnsavedChanges}
          onSaveDraft={(input) => saveAnalysis(input, goBack)}
        />
      </Show>

      <UnsavedChangesDialog when={() => !isReadOnly && readHasUnsavedChanges()} />

      <NoticeDialog
        message={t('sandBed.actions.completedMessage', { sample: detail.sampleId })}
        open={completionInput !== null}
        title={t('sandBed.actions.completedTitle')}
        onConfirm={confirmCompletion}
        onOpenChange={(open) => !open && setCompletionInput(null)}
      />
    </PageLayout>
  );
}

export const Route = createFileRoute('/seeds/sand-bed/$id')({
  validateSearch: analysisDetailSearchSchema,
  component: SandBedDetailPage,
});
